import type { ToastProps } from "@v1/components/designSystem/alert/Toast";

import { getDependencyData } from "@v1/actions/core-actions";
import { toast } from "@v1/components/designSystem/alert/Toast";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import { useDraftElementMetadata } from "@v1/hooks/useDraftElementMetadata";
import { useStoreProductsFromDraftElement } from "@v1/hooks/useStoreProducts";
import {
  selectDraftElementId,
  selectDraftElementType,
  selectIsElementUpdateInProgressOrQueued,
  selectRootComponentId,
  selectStoreUrl,
  selectTemplateShopifyProductIdsLength,
} from "@v1/reducers/core-reducer";
import { setLeftBarActiveTab } from "@v1/reducers/ui-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { getPageUrl } from "@v1/utils/element";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { elementRouter, elementUtils } from "@v2/trpc/routes/element";
import { getFromRecordOrNull } from "replo-runtime/shared/utils/optional";

const publishErrorsBeforeStartMessages = {
  elementUpdatingOrQueued: {
    title: "An update to the page is saving.",
    message: "Please publish after saving is complete.",
  },
  productsStillLoading: {
    title: "Shopify Product data is still loading.",
    message: "Please publish after Shopify Product data finishes loading.",
  },
  elementStillLoading: {
    title: "Page data is still loading.",
    message: "Please publish after page data finishes loading.",
  },
};

export default function usePublishOnClickInfo() {
  const store = useEditorStore();
  const storeUrl = useEditorSelector(selectStoreUrl);
  const draftElementId = useEditorSelector(selectDraftElementId);
  const draftElementType = useEditorSelector(selectDraftElementType);
  const elementMetadata = useDraftElementMetadata();
  const pageEditorData = elementTypeToEditorData[draftElementType];

  const logEvent = useLogAnalytics();

  const publishElement = elementRouter.publish.useMutation({
    onMutate: async () => {
      return {
        shopifyPagePath: elementMetadata?.shopifyPagePath,
      };
    },
    onSuccess: (_errs, variables, context) => {
      let toastProps: ToastProps = {
        header: `${pageEditorData.singularDisplayName} Published`,
        message: `Your ${pageEditorData.singularDisplayName} was published to Shopify`,
      };
      const bundleConfigs = Object.entries(
        variables.publishDependencies.dependencies,
      ).flatMap(([_, dependencyArray]) => {
        return dependencyArray
          .filter((dependency) => dependency.type === "bundleConfig")
          .map(({ config }) => config);
      });
      if (bundleConfigs.length > 0) {
        logEvent("bundleBuilder.settings.update", {
          bundleConfigs,
        });
      }
      if (draftElementType === "page" && context.shopifyPagePath) {
        const url = getPageUrl({
          storeUrl: storeUrl ?? undefined,
          shopifyPagePath: context.shopifyPagePath,
        });
        if (url) {
          toastProps = {
            ...toastProps,
            cta: "View Live Page",
            ctaOnClick: () => {
              window.open(url, "_blank", "noreferrer");
            },
          };
        }
      }
      toast(toastProps);

      void elementUtils.findRevisionsMetadata.invalidate({
        elementId: variables.elementId,
      });
      void elementUtils.getElementMetadataById.invalidate(variables.elementId);
      void elementUtils.listAllElementsMetadata.invalidate();
    },
    onError: (error) => {
      if (error.data.key === "errors.shopify.pageAlreadyExists") {
        dispatch(setLeftBarActiveTab("settings"));
      }
    },
  });

  const isPublishing = publishElement.isPending;

  const dispatch = useEditorDispatch();
  // Note (Noah, 2024-02-29, USE-774): It might be that products have loaded but
  // the full component for this element hasn't yet. If this is the case, we still
  // want to block publishing
  const draftElementHasLoaded = Boolean(
    useEditorSelector(selectRootComponentId),
  );
  const { isLoading: isFetchProductLoading } =
    useStoreProductsFromDraftElement();
  const numAssignedProducts = useEditorSelector(
    selectTemplateShopifyProductIdsLength,
  );

  const publish = () => {
    if (draftElementId) {
      const dependencies = getDependencyData(store.getState());
      publishElement.mutate({
        elementId: draftElementId,
        publishDependencies: {
          ...dependencies,
          elementId: draftElementId,
        },
      });
    }
  };

  const isElementUpdateInProgressOrQueued = useEditorSelector(
    selectIsElementUpdateInProgressOrQueued,
  );

  let publishNotAvailableKey:
    | keyof typeof publishErrorsBeforeStartMessages
    | null = null;
  if (isElementUpdateInProgressOrQueued) {
    publishNotAvailableKey = "elementUpdatingOrQueued";
  } else if (isFetchProductLoading) {
    publishNotAvailableKey = "productsStillLoading";
  } else if (!draftElementHasLoaded) {
    publishNotAvailableKey = "elementStillLoading";
  }
  const publishNotAvailableReason = getFromRecordOrNull(
    publishErrorsBeforeStartMessages,
    publishNotAvailableKey,
  );

  return {
    publishNotAvailableReason,
    publishErrorsBeforeStartMessages,
    draftElementType,
    numAssignedProducts,
    publish,
    isPublishing,
    pageEditorData,
  };
}
